// src/screens/s2/BusinessPL.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 2 — P&L Waterfall.
// Revenue → CoS → Gross Profit → Staff → Operating → Owner Salary → Owner
// Pension → EBITDA → Finance & Depreciation → Profit Before Tax → CT (at the
// client's effective rate) → Profit After Tax. Three headline cards below:
// gross margin %, net margin %, monthly cash available (PAT ÷ 12).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { useXeroData } from '../../hooks/useXeroData';
import { effectiveCTRate } from '../../taxConfig';
import { formatCurrency, formatPercent } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// Stacked-spacer waterfall row. Recharts stacks `spacer` (transparent) +
// `value` (coloured) so each deduction bar floats at the correct y-range.
// Anchor totals (Revenue / GP / EBITDA / PBT / PAT) sit at floor=0. Line
// overlay reads `running` to trace the trajectory through bar tops.
interface ChartRow {
  name: string;
  spacer: number;
  value: number;
  running: number;
  fill: string;
}

const FILL = {
  revenue: '#1E3A5F',
  deduction: '#DC2626',
  subtotal: '#60A5FA',
  ebitda: '#1B2B4B',
  pat: '#C9A84C',
} as const;

export default function BusinessPL({ data }: Props) {
  const xero = useXeroData(data.xeroTenantId);

  // Render an empty state when Xero isn't available — S2 needs live data.
  if (xero.loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-sm text-gray-500">Loading business data from Xero…</div>
      </div>
    );
  }
  if (!xero.mapped || !xero.summary) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold text-ma-navy mb-4">P&L Waterfall</h2>
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-300 p-5 text-sm text-amber-800">
          {xero.error
            ? <>Xero unavailable ({xero.error}). The P&L waterfall requires live Xero data.</>
            : <>Chart of accounts not yet mapped — open the COA Mapper to categorise your Xero accounts before this session.</>}
        </div>
      </div>
    );
  }

  const s = xero.summary;

  // ── Waterfall construction ─────────────────────────────────────────────
  // All figures annual; presentation layer scales to monthly at the end.
  const revenue = s.revenue;
  const cogs = s.costOfSales;
  const grossProfit = s.grossProfit;
  const staff = s.staffCosts;
  const operating = s.totalOperating;   // excludes staff + owner salary per data.js contract
  const ownerSalary = s.ownerSalary;
  const ownerPension = s.ownerPension;
  const ebitda = s.ebitda;
  const financeDep = s.financeAndDepreciation;
  const pbt = s.netProfit;              // pre-CT profit (API naming is misleading)
  const ctRate = effectiveCTRate(pbt);
  const ct = Math.max(0, pbt * ctRate);
  const pat = pbt - ct;

  // Build the waterfall using the stacked-spacer pattern. Running total is
  // threaded through the loop; anchors re-display the current running value
  // as a full-height bar from 0, deductions float between before/after.
  const chartData: ChartRow[] = [];
  let running = 0;

  const pushAnchor = (name: string, fill: string) => {
    chartData.push({ name, spacer: 0, value: running, running, fill });
  };
  const pushDeduction = (name: string, amount: number, fill: string) => {
    const before = running;
    const after = before - amount;
    chartData.push({
      name,
      spacer: Math.min(before, after),
      value: Math.abs(before - after),
      running: after,
      fill,
    });
    running = after;
  };

  running = revenue;
  pushAnchor('Revenue', FILL.revenue);
  pushDeduction('Cost of Sales', cogs, FILL.deduction);
  pushAnchor('Gross Profit', FILL.subtotal);
  pushDeduction('Staff', staff, FILL.deduction);
  pushDeduction('Operating', operating, FILL.deduction);
  pushDeduction('Owner Salary', ownerSalary, FILL.deduction);
  pushDeduction('Owner Pension', ownerPension, FILL.deduction);
  pushAnchor('EBITDA', FILL.ebitda);
  pushDeduction('Finance & Dep.', financeDep, FILL.deduction);
  pushAnchor('Profit Pre-Tax', FILL.subtotal);
  pushDeduction(`CT @${(ctRate * 100).toFixed(1)}%`, ct, FILL.deduction);
  pushAnchor('Profit After Tax', FILL.pat);

  const grossMarginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
  const netMarginPct = revenue > 0 ? (pat / revenue) * 100 : 0;
  const monthlyAvailable = pat / 12;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* HEADER */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">
          P&amp;L Waterfall
          {xero.tenantName && (
            <span className="ml-2 text-sm font-normal text-gray-500">— {xero.tenantName}</span>
          )}
        </h2>
        <p className="text-gray-500 mt-1 text-sm">
          Every pound earned, traced through to what lands in retained earnings
          after corporation tax.
        </p>
      </div>

      {/* HEADLINE CARDS */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Gross margin
          </div>
          <div className="text-3xl font-bold text-ma-navy">{formatPercent(grossMarginPct)}</div>
          <p className="text-xs text-gray-500 mt-2">
            {formatCurrency(grossProfit)} gross on {formatCurrency(revenue)} revenue.
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Net margin
          </div>
          <div className="text-3xl font-bold text-ma-navy">{formatPercent(netMarginPct)}</div>
          <p className="text-xs text-gray-500 mt-2">
            {formatCurrency(pat)} after CT @ {(ctRate * 100).toFixed(1)}%.
          </p>
        </div>
        <div
          className="rounded-2xl border-2 p-5"
          style={{ backgroundColor: '#FFF9ED', borderColor: '#C9A84C' }}
        >
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#8B7A3E' }}>
            Monthly cash available
          </div>
          <div className="text-3xl font-bold" style={{ color: '#C9A84C' }}>
            {formatCurrency(monthlyAvailable)}
          </div>
          <p className="text-xs mt-2" style={{ color: '#8B7A3E' }}>
            PAT ÷ 12 — what's left for reserves, growth, extraction.
          </p>
        </div>
      </div>

      {/* WATERFALL */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-4">
          Annual waterfall
        </h3>
        <div style={{ width: '100%', height: 380 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 20, right: 20, bottom: 40, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10 }}
                stroke="#9CA3AF"
                angle={-30}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip
                formatter={(v: any) => formatCurrency(Number(v))}
                labelFormatter={(name) => name}
              />
              <ReferenceLine y={0} stroke="#9CA3AF" />
              {/* Stacked-spacer waterfall: transparent spacer floats the
                  visible value bar to its correct y-range. Cells still
                  override fill per-bar. Line overlay traces the running
                  trajectory through bar tops. */}
              <Bar dataKey="spacer" stackId="wf" fill="transparent" />
              <Bar dataKey="value" stackId="wf" fill={FILL.revenue} radius={[4, 4, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="running"
                stroke="#94A3B8"
                strokeWidth={2}
                strokeDasharray="4 2"
                dot={{ r: 3, fill: '#94A3B8' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Bars show the absolute magnitude of each step (red = deduction, navy/gold = anchor totals).
          Dashed line traces the running balance through the waterfall.
        </p>
      </div>

      {/* FOOTER NOTE */}
      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
        <p className="text-xs text-gray-600 leading-relaxed">
          CT applied at {formatPercent(ctRate * 100)} — the effective rate for a
          profit of {formatCurrency(pbt)} under HMRC marginal relief
          (19% up to £50k, 25% at £250k+, tapered between).
        </p>
      </div>
    </div>
  );
}
